// src/lib/command-palette-store.ts

const PINNED_COMMANDS_KEY = 'commandPalette_pinnedCommands';
const RECENT_COMMANDS_KEY = 'commandPalette_recentCommands';
const MAX_RECENT_COMMANDS = 10; // Maximum number of recent commands to store

// --- Helper Functions ---

function safeJsonParse<T>(key: string, defaultValue: T): T {
  try {
    const item = localStorage.getItem(key);
    return item ? (JSON.parse(item) as T) : defaultValue;
  } catch (error) {
    console.error(`Error parsing localStorage key "${key}":`, error);
    // Optionally clear the corrupted item
    // localStorage.removeItem(key);
    return defaultValue;
  }
}

function safeJsonStringify<T>(key: string, value: T): void {
  try {
    const stringified = JSON.stringify(value);
    localStorage.setItem(key, stringified);
  } catch (error) {
    console.error(`Error stringifying value for localStorage key "${key}":`, error);
  }
}

// --- Pinned Commands ---

/**
 * Retrieves the list of pinned command IDs from localStorage.
 * @returns An array of pinned command IDs.
 */
export function getPinnedCommandIds(): string[] {
  return safeJsonParse<string[]>(PINNED_COMMANDS_KEY, []);
}

/**
 * Adds a command ID to the list of pinned commands in localStorage.
 * Does nothing if the ID is already pinned.
 * @param id The command ID to pin.
 */
export function addPinnedCommandId(id: string): void {
  const currentPinned = getPinnedCommandIds();
  if (!currentPinned.includes(id)) {
    const updatedPinned = [...currentPinned, id];
    safeJsonStringify(PINNED_COMMANDS_KEY, updatedPinned);
  }
}

/**
 * Removes a command ID from the list of pinned commands in localStorage.
 * @param id The command ID to unpin.
 */
export function removePinnedCommandId(id: string): void {
  const currentPinned = getPinnedCommandIds();
  const updatedPinned = currentPinned.filter(pinnedId => pinnedId !== id);
  safeJsonStringify(PINNED_COMMANDS_KEY, updatedPinned);
}

/**
 * Toggles the pinned state of a command ID.
 * @param id The command ID to toggle.
 * @returns The new pinned state (true if pinned, false if unpinned).
 */
export function togglePinnedCommandId(id: string): boolean {
    const currentPinned = getPinnedCommandIds();
    if (currentPinned.includes(id)) {
        removePinnedCommandId(id);
        return false;
    } else {
        addPinnedCommandId(id);
        return true;
    }
}


// --- Recent Commands ---

/**
 * Retrieves the list of recently used command IDs from localStorage.
 * @returns An array of recent command IDs, ordered from most to least recent.
 */
export function getRecentCommandIds(): string[] {
  return safeJsonParse<string[]>(RECENT_COMMANDS_KEY, []);
}

/**
 * Adds a command ID to the list of recently used commands in localStorage.
 * Moves the ID to the front if it already exists.
 * Limits the list to MAX_RECENT_COMMANDS.
 * @param id The command ID to add.
 */
export function addRecentCommandId(id: string): void {
  const currentRecent = getRecentCommandIds();
  // Remove the id if it already exists to move it to the front
  const filteredRecent = currentRecent.filter(recentId => recentId !== id);
  // Add the new id to the beginning and slice to maintain max length
  const updatedRecent = [id, ...filteredRecent].slice(0, MAX_RECENT_COMMANDS);
  safeJsonStringify(RECENT_COMMANDS_KEY, updatedRecent);
}